import { useState, useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DocumentUpload } from "@/components/DocumentUpload";
import { JobScrapingService } from "@/services/jobScrapingService";
import { toast } from "sonner";
import {
  Link2,
  FileText,
  Loader2,
  Sparkles,
  Lock,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Target,
  Wand2,
  Mail,
  Search,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Free homepage "Resume Match Score" tester.
//  • 3 free scores per visitor (localStorage gated)
//  • the score itself is shown free; the detailed report unlocks with an email
//  • the "tailor" CTA routes into a free account (tailoring is the paid product)
// ---------------------------------------------------------------------------

const FREE_SCORE_LIMIT = 3;
const LS_SCORES = "tw_rmt_scores";
const LS_EMAIL = "tw_rmt_email";
const LS_TAILOR = "tw_rmt_tailor_used";

interface MatchResult {
  ats_match_score?: { score?: number; pass_fail?: string; threshold?: number; explanation?: string };
  human_eye_score?: { score?: number; pass_fail?: string; explanation?: string; scan_time_seconds?: number };
  missing_keywords?: Array<{ keyword?: string; importance?: string; where_to_add?: string; context?: string }>;
  weak_sections?: Array<{ section_name?: string; issue?: string; severity?: string }>;
  top_recommended_changes?: Array<{
    rank?: number;
    change_title?: string;
    change_description?: string;
    estimated_score_improvement?: number;
    section_affected?: string;
  }>;
  tailoring_verdict?: {
    verdict?: string;
    verdict_reasoning?: string;
  };
}

interface TailoredDoc {
  HeadlineTitle?: string;
  HeadlineMetrics?: string;
  Summary?: string;
  SkillRows?: Array<Record<string, string>>;
  CareerHighlights?: Array<{ chtitle?: string; chdescription?: string }>;
  ProfessionalExperience?: Array<{
    job_title?: string;
    company?: string;
    location?: string;
    start_date?: string;
    end_date?: string;
    Descriptions?: Array<{ Description?: string }>;
  }>;
  Education?: Array<{
    College_Degree_Name?: string;
    Major?: string;
    College_Name?: string;
    City?: string;
    ST?: string;
    Year_Earned?: string;
  }>;
}

interface TailoredResume {
  clientInfo?: {
    fullName?: string;
    phoneNumber?: string;
    email?: string;
    cityST?: string;
    linkedin?: string;
  };
  data: TailoredDoc;
}

type Phase = "input" | "scoring" | "results" | "limit";
type TailorPhase = "idle" | "tailoring" | "done";

const SCORING_STEPS = [
  "Reading the job posting",
  "Scanning your resume",
  "Checking ATS keyword match",
  "Scoring recruiter fit",
  "Building your report",
];

function getScoresUsed(): number {
  const raw = Number(localStorage.getItem(LS_SCORES) || "0");
  return Number.isFinite(raw) ? raw : 0;
}

function scoreColor(score: number): string {
  if (score >= 80) return "#16A34A";
  if (score >= 56) return "#C9A84C";
  if (score >= 30) return "#EA8C2B";
  return "#DC2626";
}

function scoreLabel(score: number): string {
  if (score >= 80) return "Strong Match";
  if (score >= 56) return "Good Match";
  if (score >= 30) return "Fair Match";
  return "Weak Match";
}

/** Crisp SVG progress ring. */
function ScoreRing({ score }: { score: number }) {
  const size = 168;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const color = scoreColor(pct);
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ - (pct / 100) * circ}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[44px] font-extrabold leading-none" style={{ color }}>
          {pct}%
        </span>
        <span className="text-[12px] font-bold uppercase tracking-wide mt-1" style={{ color }}>
          {scoreLabel(pct)}
        </span>
      </div>
    </div>
  );
}

function importanceBadge(importance?: string): string {
  switch ((importance || "").toLowerCase()) {
    case "critical":
      return "bg-red-100 text-red-700";
    case "important":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function severityBadge(severity?: string): string {
  switch ((severity || "").toLowerCase()) {
    case "high":
      return "bg-red-100 text-red-700";
    case "medium":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

/** Flatten the tailor engine's row-based skills ({Skill1..4}) into a flat list. */
function flattenSkills(rows?: Array<Record<string, string>>): string[] {
  if (!Array.isArray(rows)) return [];
  const out: string[] = [];
  for (const row of rows) {
    for (const v of Object.values(row || {})) {
      if (typeof v === "string" && v.trim()) out.push(v.trim());
    }
  }
  return out;
}

function ResumeSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4">
      <h5 className="text-[12px] font-extrabold uppercase tracking-wide text-[#0B1C3F] border-b border-slate-200 pb-1 mb-2">
        {title}
      </h5>
      {children}
    </div>
  );
}

/** Renders the tailored resume returned by tailor-resume-claude-v1. */
function TailoredResumePreview({ resume }: { resume: TailoredResume }) {
  const c = resume.clientInfo || {};
  const d = resume.data || {};
  const skills = flattenSkills(d.SkillRows);
  const contact = [c.cityST, c.phoneNumber, c.email].filter(Boolean).join("  •  ");
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 sm:p-7 text-left">
      {/* Header */}
      <div className="text-center border-b border-slate-200 pb-3">
        <h3 className="text-[20px] font-extrabold text-[#0B1C3F]">{c.fullName || "Your Name"}</h3>
        {contact && <p className="mt-0.5 text-[12px] text-slate-500">{contact}</p>}
        {d.HeadlineTitle && (
          <p className="mt-2 text-[14px] font-bold text-[#C9A84C]">{d.HeadlineTitle}</p>
        )}
        {d.HeadlineMetrics && <p className="text-[12px] text-slate-600">{d.HeadlineMetrics}</p>}
      </div>

      {d.Summary && (
        <ResumeSection title="Professional Summary">
          <p className="text-[13px] leading-relaxed text-slate-700">{d.Summary}</p>
        </ResumeSection>
      )}

      {skills.length > 0 && (
        <ResumeSection title="Core Skills">
          <div className="flex flex-wrap gap-1.5">
            {skills.map((s, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded bg-slate-100 text-[12px] font-medium text-slate-700"
              >
                {s}
              </span>
            ))}
          </div>
        </ResumeSection>
      )}

      {d.CareerHighlights && d.CareerHighlights.length > 0 && (
        <ResumeSection title="Career Highlights">
          <div className="space-y-2">
            {d.CareerHighlights.map((h, i) => (
              <div key={i}>
                <p className="text-[13px] font-bold text-[#0B1C3F]">{h.chtitle}</p>
                <p className="text-[13px] text-slate-600 leading-relaxed">{h.chdescription}</p>
              </div>
            ))}
          </div>
        </ResumeSection>
      )}

      {d.ProfessionalExperience && d.ProfessionalExperience.length > 0 && (
        <ResumeSection title="Professional Experience">
          <div className="space-y-4">
            {d.ProfessionalExperience.map((job, i) => (
              <div key={i}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-[14px] font-bold text-[#0B1C3F]">{job.job_title}</span>
                  <span className="text-[12px] text-slate-500">
                    {[job.start_date, job.end_date].filter(Boolean).join(" – ")}
                  </span>
                </div>
                <p className="text-[12px] font-medium text-slate-500">
                  {[job.company, job.location].filter(Boolean).join("  •  ")}
                </p>
                <ul className="mt-1.5 space-y-1">
                  {(job.Descriptions || []).map((b, j) => (
                    <li
                      key={j}
                      className="flex gap-2 text-[13px] text-slate-700 leading-relaxed"
                    >
                      <span className="text-[#C9A84C] flex-shrink-0">▸</span>
                      <span>{b.Description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </ResumeSection>
      )}

      {d.Education && d.Education.length > 0 && (
        <ResumeSection title="Education">
          <div className="space-y-1">
            {d.Education.map((e, i) => (
              <p key={i} className="text-[13px] text-slate-700">
                <span className="font-bold text-[#0B1C3F]">
                  {[e.College_Degree_Name, e.Major].filter(Boolean).join(", ")}
                </span>
                {(e.College_Name || e.City || e.ST) && " — "}
                {[e.College_Name, [e.City, e.ST].filter(Boolean).join(", ")]
                  .filter(Boolean)
                  .join(", ")}
                {e.Year_Earned ? ` (${e.Year_Earned})` : ""}
              </p>
            ))}
          </div>
        </ResumeSection>
      )}
    </div>
  );
}

export function HomeResumeMatchTester() {
  const [phase, setPhase] = useState<Phase>("input");
  const [jobMode, setJobMode] = useState<"paste" | "url">("paste");
  const [jobText, setJobText] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [resumeMode, setResumeMode] = useState<"paste" | "upload">("paste");
  const [resumeText, setResumeText] = useState("");
  const [resumeFileName, setResumeFileName] = useState("");
  const [result, setResult] = useState<MatchResult | null>(null);
  const [scoringStep, setScoringStep] = useState(0);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [scoresUsed, setScoresUsed] = useState(0);
  const [resolvedJobText, setResolvedJobText] = useState("");
  const [tailorUsed, setTailorUsed] = useState(false);
  const [tailorPhase, setTailorPhase] = useState<TailorPhase>("idle");
  const [tailorResult, setTailorResult] = useState<TailoredResume | null>(null);

  const stepTimer = useRef<number | null>(null);

  // Mount: read prior state, and silently establish a session so file
  // uploads (which need a Supabase user) work for anonymous visitors.
  useEffect(() => {
    setScoresUsed(getScoresUsed());
    if (localStorage.getItem(LS_EMAIL)) setEmailUnlocked(true);
    if (localStorage.getItem(LS_TAILOR)) setTailorUsed(true);

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          await supabase.auth.signInAnonymously();
        }
      } catch {
        /* anonymous auth optional — paste still works without it */
      }
    })();
  }, []);

  // Cycle the "analyzing" steps while scoring.
  useEffect(() => {
    if (phase === "scoring") {
      setScoringStep(0);
      stepTimer.current = window.setInterval(() => {
        setScoringStep((s) => Math.min(s + 1, SCORING_STEPS.length - 1));
      }, 2600);
    }
    return () => {
      if (stepTimer.current) window.clearInterval(stepTimer.current);
    };
  }, [phase]);

  const handleUploadComplete = (text: string, filename?: string) => {
    if (text && text.trim().length > 0) {
      setResumeText(text);
      setResumeFileName(filename || "resume");
      toast.success("Resume loaded");
    } else {
      toast.error("We couldn't read that file. Try pasting your resume text instead.");
    }
  };

  const runScore = async () => {
    if (getScoresUsed() >= FREE_SCORE_LIMIT) {
      setPhase("limit");
      return;
    }

    const jobReady = jobMode === "url" ? jobUrl.trim().length > 0 : jobText.trim().length > 40;
    if (!jobReady) {
      toast.error(
        jobMode === "url" ? "Paste a job posting URL first." : "Paste the job description first.",
      );
      return;
    }
    if (resumeText.trim().length < 80) {
      toast.error("Add your resume — paste the text or upload your file.");
      return;
    }

    setPhase("scoring");
    setResult(null);

    try {
      // 1. Resolve the job description text.
      let jobDescriptionText = jobText.trim();
      if (jobMode === "url") {
        const scrape = await JobScrapingService.scrapeJobUrl(jobUrl.trim());
        if (!scrape.success || !scrape.content) {
          throw new Error(
            "We couldn't read that job URL. Paste the job description text instead.",
          );
        }
        jobDescriptionText = scrape.content;
      }
      setResolvedJobText(jobDescriptionText);

      // 2. Run the match analysis (same engine used inside TailorWiz).
      //    Retry once — guards against a rare bad response from the model.
      let analyzed: MatchResult | null = null;
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase.functions.invoke("analyze-resume-match", {
          body: {
            action: "resume_job_match",
            resumeText: resumeText.trim(),
            job_description_text: jobDescriptionText,
            report_version: "snapshot",
          },
        });
        if (!error && data?.result?.ats_match_score) {
          analyzed = data.result as MatchResult;
          break;
        }
        lastError = error || new Error(data?.error || "Analysis did not return a score.");
      }
      if (!analyzed) {
        throw lastError instanceof Error
          ? lastError
          : new Error("Analysis did not return a score.");
      }

      setResult(analyzed);
      const next = getScoresUsed() + 1;
      localStorage.setItem(LS_SCORES, String(next));
      setScoresUsed(next);
      setPhase("results");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(msg.includes("non-2xx") ? "We couldn't analyze that. Please try again." : msg);
      setPhase("input");
    }
  };

  const handleUnlock = async () => {
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setUnlocking(true);
    try {
      localStorage.setItem(LS_EMAIL, clean);
      setEmailUnlocked(true);
      toast.success("Full report unlocked");
      // Best-effort lead capture — never blocks the unlock.
      const score = result?.ats_match_score?.score ?? "n/a";
      supabase.functions
        .invoke("send-contact-message", {
          body: {
            name: "Resume Match Tool Lead",
            email: clean,
            subject: "New lead — free Resume Match Score tool",
            message:
              `A visitor used the free Resume Match Score tool on tailorwiz.com and ` +
              `unlocked their full report.\n\nEmail: ${clean}\nMatch score: ${score}%`,
          },
        })
        .catch(() => {
          /* lead email is best-effort */
        });
    } finally {
      setUnlocking(false);
    }
  };

  // One free tailored resume per visitor.
  const handleTailor = async () => {
    if (localStorage.getItem(LS_TAILOR)) {
      setTailorUsed(true);
      return;
    }
    if (!resumeText.trim() || !resolvedJobText.trim()) {
      toast.error("Run a match score first, then tailor.");
      return;
    }
    setTailorPhase("tailoring");
    try {
      // Retry once — guards against a rare bad response from the model.
      let tailored: TailoredResume | null = null;
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { data, error } = await supabase.functions.invoke("tailor-resume-claude-v1", {
          body: {
            resumeText: resumeText.trim(),
            jobDescription: resolvedJobText,
            companyName: "",
            jobTitle: "",
            tailoringSource: "job-description",
          },
        });
        const doc = data?.Resume_JSON?.documents?.[0]?.data;
        if (!error && data?.success && doc) {
          tailored = { clientInfo: data.Resume_JSON.clientInfo, data: doc };
          break;
        }
        lastError = error || new Error(data?.error || "Tailoring did not return a resume.");
      }
      if (!tailored) {
        throw lastError instanceof Error ? lastError : new Error("Tailoring failed.");
      }
      setTailorResult(tailored);
      localStorage.setItem(LS_TAILOR, "1");
      setTailorUsed(true);
      setTailorPhase("done");
      toast.success("Your resume has been tailored");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Tailoring failed.";
      toast.error(
        msg.includes("non-2xx") ? "We couldn't tailor that. Please try again." : msg,
      );
      setTailorPhase("idle");
    }
  };

  const resetForAnother = () => {
    setResult(null);
    setJobText("");
    setJobUrl("");
    setResumeText("");
    setResumeFileName("");
    setResolvedJobText("");
    setTailorResult(null);
    setTailorPhase("idle");
    setPhase(getScoresUsed() >= FREE_SCORE_LIMIT ? "limit" : "input");
  };

  const remaining = Math.max(0, FREE_SCORE_LIMIT - scoresUsed);

  return (
    <section className="bg-slate-50 border-y border-slate-200 py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0B1C3F] text-[#C9A84C] text-[12px] font-bold uppercase tracking-wide">
            <Sparkles className="w-3.5 h-3.5" /> Free Resume Check
          </span>
          <h2 className="mt-4 text-[30px] sm:text-[40px] font-extrabold tracking-tight leading-[1.1] text-[#0B1C3F]">
            Test Your Resume Against <span className="text-[#C9A84C]">Any Job</span>
          </h2>
          <p className="mt-3 text-[16px] sm:text-[17px] text-slate-600 leading-relaxed">
            Paste a job link or the description, add your resume, and see exactly how you
            score against the ATS — in under a minute. The same analysis our members use
            inside TailorWiz.
          </p>
        </div>

        {/* Card */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          {/* ============ INPUT ============ */}
          {phase === "input" && (
            <div className="p-6 sm:p-8">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Job */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded-lg bg-[#0B1C3F] flex items-center justify-center">
                      <Target className="w-4 h-4 text-[#C9A84C]" />
                    </span>
                    <h3 className="text-[15px] font-bold text-[#0B1C3F]">The Job You Want</h3>
                  </div>
                  <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-3">
                    <button
                      type="button"
                      onClick={() => setJobMode("paste")}
                      className={`flex-1 text-[13px] font-semibold py-1.5 rounded-md transition-colors ${
                        jobMode === "paste" ? "bg-white text-[#0B1C3F] shadow-sm" : "text-slate-500"
                      }`}
                    >
                      Paste description
                    </button>
                    <button
                      type="button"
                      onClick={() => setJobMode("url")}
                      className={`flex-1 text-[13px] font-semibold py-1.5 rounded-md transition-colors ${
                        jobMode === "url" ? "bg-white text-[#0B1C3F] shadow-sm" : "text-slate-500"
                      }`}
                    >
                      Job URL
                    </button>
                  </div>
                  {jobMode === "paste" ? (
                    <Textarea
                      value={jobText}
                      onChange={(e) => setJobText(e.target.value)}
                      placeholder="Paste the full job posting here — title, responsibilities, requirements..."
                      className="min-h-[220px] resize-none text-[14px]"
                    />
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={jobUrl}
                          onChange={(e) => setJobUrl(e.target.value)}
                          placeholder="https://company.com/careers/job-posting"
                          className="pl-9 h-11 text-[14px]"
                        />
                      </div>
                      <p className="text-[12px] text-slate-500 leading-relaxed">
                        Works with LinkedIn, Indeed, ZipRecruiter, Glassdoor and most company
                        career pages. If a site blocks us, just paste the description instead.
                      </p>
                    </div>
                  )}
                </div>

                {/* Resume */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded-lg bg-[#0B1C3F] flex items-center justify-center">
                      <FileText className="w-4 h-4 text-[#C9A84C]" />
                    </span>
                    <h3 className="text-[15px] font-bold text-[#0B1C3F]">Your Resume</h3>
                  </div>
                  <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-3">
                    <button
                      type="button"
                      onClick={() => setResumeMode("paste")}
                      className={`flex-1 text-[13px] font-semibold py-1.5 rounded-md transition-colors ${
                        resumeMode === "paste" ? "bg-white text-[#0B1C3F] shadow-sm" : "text-slate-500"
                      }`}
                    >
                      Paste text
                    </button>
                    <button
                      type="button"
                      onClick={() => setResumeMode("upload")}
                      className={`flex-1 text-[13px] font-semibold py-1.5 rounded-md transition-colors ${
                        resumeMode === "upload" ? "bg-white text-[#0B1C3F] shadow-sm" : "text-slate-500"
                      }`}
                    >
                      Upload file
                    </button>
                  </div>
                  {resumeMode === "paste" ? (
                    <Textarea
                      value={resumeText}
                      onChange={(e) => {
                        setResumeText(e.target.value);
                        setResumeFileName("");
                      }}
                      placeholder="Paste the full text of your resume here..."
                      className="min-h-[220px] resize-none text-[14px]"
                    />
                  ) : (
                    <div>
                      <DocumentUpload
                        onUploadComplete={handleUploadComplete}
                        title="Upload Resume"
                        description="PDF, Word, or text file"
                        acceptedTypes={[".pdf", ".doc", ".docx", ".txt"]}
                      />
                      {resumeText && resumeFileName && (
                        <p className="mt-2 text-[12px] text-green-700 font-semibold flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Loaded {resumeFileName} ({resumeText.trim().split(/\s+/).length} words)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Run */}
              <div className="mt-6 flex flex-col items-center gap-2">
                <Button
                  onClick={runScore}
                  className="h-13 px-8 py-3.5 text-[16px] font-bold bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A] hover:shadow-lg hover:shadow-[#C9A84C]/30 transition-all"
                >
                  <Search className="w-5 h-5 mr-2" />
                  See My Match Score — Free
                </Button>
                <p className="text-[12px] text-slate-500">
                  {remaining > 0
                    ? `${remaining} of ${FREE_SCORE_LIMIT} free checks left · No credit card`
                    : "You've used your free checks — create an account for unlimited"}
                </p>
              </div>
            </div>
          )}

          {/* ============ SCORING ============ */}
          {phase === "scoring" && (
            <div className="p-10 sm:p-16 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#0B1C3F] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#C9A84C] animate-spin" />
              </div>
              <h3 className="mt-5 text-[20px] font-extrabold text-[#0B1C3F]">
                Analyzing your match…
              </h3>
              <div className="mt-5 max-w-xs mx-auto space-y-2 text-left">
                {SCORING_STEPS.map((step, i) => (
                  <div key={step} className="flex items-center gap-2.5 text-[14px]">
                    {i < scoringStep ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : i === scoringStep ? (
                      <Loader2 className="w-4 h-4 text-[#C9A84C] animate-spin flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                    )}
                    <span className={i <= scoringStep ? "text-[#0B1C3F] font-medium" : "text-slate-400"}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[12px] text-slate-500">This usually takes 20–40 seconds.</p>
            </div>
          )}

          {/* ============ RESULTS ============ */}
          {phase === "results" && result && (
            <div className="p-6 sm:p-8">
              {/* Score hero */}
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 bg-slate-50 rounded-xl border border-slate-200 p-6">
                <ScoreRing score={result.ats_match_score?.score ?? 0} />
                <div className="text-center sm:text-left flex-1">
                  <h3 className="text-[20px] font-extrabold text-[#0B1C3F]">
                    Your Resume Match Score
                  </h3>
                  <p className="mt-1.5 text-[14px] text-slate-600 leading-relaxed">
                    {result.tailoring_verdict?.verdict_reasoning ||
                      result.ats_match_score?.explanation ||
                      "Here's how your resume stacks up against this job."}
                  </p>
                  <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 text-[12px] font-semibold text-slate-700">
                      Recruiter-fit score: {result.human_eye_score?.score ?? "—"}%
                    </span>
                    {result.tailoring_verdict?.verdict && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0B1C3F] text-[#C9A84C] text-[12px] font-bold">
                        {result.tailoring_verdict.verdict}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed report */}
              <div className="mt-6">
                <h4 className="text-[16px] font-extrabold text-[#0B1C3F] flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9A84C]" />
                  Your Full Report
                </h4>

                {emailUnlocked ? (
                  <div className="mt-4 space-y-6">
                    {/* Missing keywords */}
                    {result.missing_keywords && result.missing_keywords.length > 0 && (
                      <div>
                        <h5 className="text-[14px] font-bold text-[#0B1C3F] mb-2">
                          Missing Keywords ({result.missing_keywords.length})
                        </h5>
                        <div className="space-y-2">
                          {result.missing_keywords.map((kw, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white"
                            >
                              <span
                                className={`px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap ${importanceBadge(
                                  kw.importance,
                                )}`}
                              >
                                {kw.importance || "Keyword"}
                              </span>
                              <div className="min-w-0">
                                <p className="text-[14px] font-semibold text-[#0B1C3F]">
                                  {kw.keyword}
                                </p>
                                <p className="text-[13px] text-slate-600">
                                  {kw.context}
                                  {kw.where_to_add ? ` — add to ${kw.where_to_add}.` : ""}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Weak sections */}
                    {result.weak_sections && result.weak_sections.length > 0 && (
                      <div>
                        <h5 className="text-[14px] font-bold text-[#0B1C3F] mb-2">
                          Weak Sections
                        </h5>
                        <div className="space-y-2">
                          {result.weak_sections.map((ws, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white"
                            >
                              <span
                                className={`px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap ${severityBadge(
                                  ws.severity,
                                )}`}
                              >
                                {ws.severity || "Issue"}
                              </span>
                              <div className="min-w-0">
                                <p className="text-[14px] font-semibold text-[#0B1C3F]">
                                  {ws.section_name}
                                </p>
                                <p className="text-[13px] text-slate-600">{ws.issue}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommended changes */}
                    {result.top_recommended_changes &&
                      result.top_recommended_changes.length > 0 && (
                        <div>
                          <h5 className="text-[14px] font-bold text-[#0B1C3F] mb-2">
                            Top Fixes to Raise Your Score
                          </h5>
                          <div className="space-y-2">
                            {result.top_recommended_changes.map((rc, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white"
                              >
                                <span className="w-6 h-6 rounded-full bg-[#0B1C3F] text-[#C9A84C] text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                                  {rc.rank || i + 1}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-[14px] font-semibold text-[#0B1C3F]">
                                    {rc.change_title}
                                    {rc.estimated_score_improvement ? (
                                      <span className="ml-2 text-[12px] font-bold text-green-700">
                                        +{rc.estimated_score_improvement} pts
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className="text-[13px] text-slate-600">
                                    {rc.change_description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  /* Email gate */
                  <div className="mt-4 relative">
                    {/* blurred skeleton */}
                    <div className="space-y-2 blur-[6px] select-none pointer-events-none" aria-hidden>
                      {[80, 95, 70, 88, 60].map((w, i) => (
                        <div key={i} className="p-3 rounded-lg border border-slate-200 bg-white">
                          <div className="h-3 rounded bg-slate-200" style={{ width: `${w}%` }} />
                          <div className="h-2.5 rounded bg-slate-100 mt-2" style={{ width: `${w - 20}%` }} />
                        </div>
                      ))}
                    </div>
                    {/* overlay */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div className="w-full max-w-md bg-white rounded-xl border-2 border-[#C9A84C]/40 shadow-xl p-6 text-center">
                        <span className="w-11 h-11 mx-auto rounded-full bg-[#0B1C3F] flex items-center justify-center">
                          <Lock className="w-5 h-5 text-[#C9A84C]" />
                        </span>
                        <h5 className="mt-3 text-[17px] font-extrabold text-[#0B1C3F]">
                          Unlock Your Full Report
                        </h5>
                        <p className="mt-1 text-[13px] text-slate-600">
                          See every missing keyword, weak section, and the exact fixes to raise
                          your score. Enter your email to unlock it free.
                        </p>
                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                          <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
                              placeholder="you@example.com"
                              className="pl-9 h-11 text-[14px]"
                            />
                          </div>
                          <Button
                            onClick={handleUnlock}
                            disabled={unlocking}
                            className="h-11 px-5 font-bold bg-[#0B1C3F] text-white hover:bg-[#0E2350]"
                          >
                            {unlocking ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Unlock Report"
                            )}
                          </Button>
                        </div>
                        <p className="mt-2 text-[11px] text-slate-400">
                          No spam. Just your report and the occasional resume tip.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ===== Tailor — one free tailored resume per visitor ===== */}
              {tailorPhase === "done" && tailorResult ? (
                <div className="mt-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Wand2 className="w-5 h-5 text-[#C9A84C]" />
                    <h4 className="text-[16px] font-extrabold text-[#0B1C3F]">
                      Your Tailored Resume
                    </h4>
                    <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-[11px] font-bold">
                      Free tailor used
                    </span>
                  </div>
                  <TailoredResumePreview resume={tailorResult} />
                  <div className="mt-4 rounded-xl bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] p-5 text-center">
                    <p className="text-[14px] text-slate-200 max-w-xl mx-auto">
                      That was your free tailor. Create a free account to download the formatted
                      Word version, tailor unlimited resumes, and generate a matching cover letter
                      &amp; LinkedIn profile.
                    </p>
                    <Link
                      to="/signup"
                      className="mt-3 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] transition-colors"
                    >
                      Create My Free Account <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ) : tailorPhase === "tailoring" ? (
                <div className="mt-6 rounded-xl bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] p-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-white/10 flex items-center justify-center">
                    <Loader2 className="w-7 h-7 text-[#C9A84C] animate-spin" />
                  </div>
                  <h4 className="mt-4 text-[18px] font-extrabold text-white">
                    Tailoring your resume…
                  </h4>
                  <p className="mt-1.5 text-[13px] text-slate-300">
                    Rewriting your summary, bullets, and skills to match this job. This takes
                    about 30 seconds.
                  </p>
                </div>
              ) : tailorUsed ? (
                <div className="mt-6 rounded-xl bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] p-6 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Wand2 className="w-5 h-5 text-[#C9A84C]" />
                    <h4 className="text-[18px] font-extrabold text-white">
                      You've used your free tailor
                    </h4>
                  </div>
                  <p className="mt-1.5 text-[14px] text-slate-300 max-w-lg mx-auto">
                    Create a free account to tailor unlimited resumes — every one rewritten to
                    90%+ match before you apply.
                  </p>
                  <Link
                    to="/signup"
                    className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] transition-colors"
                  >
                    Create My Free Account <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="mt-6 rounded-xl bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] p-6 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Wand2 className="w-5 h-5 text-[#C9A84C]" />
                    <h4 className="text-[18px] font-extrabold text-white">
                      Want this resume rewritten to match the job?
                    </h4>
                  </div>
                  <p className="mt-1.5 text-[14px] text-slate-300 max-w-lg mx-auto">
                    TailorWiz rewrites every bullet, your summary, and your skills to match this
                    exact role. Your first tailor is on us — no account needed.
                  </p>
                  <Button
                    onClick={handleTailor}
                    className="mt-4 h-12 px-7 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] transition-colors"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Tailor My Resume — Free
                  </Button>
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={resetForAnother}
                  className="text-[13px] font-semibold text-[#0B1C3F] hover:text-[#C9A84C] transition-colors"
                >
                  {remaining > 0
                    ? `Score another resume (${remaining} free left)`
                    : "Score another resume"}
                </button>
              </div>
            </div>
          )}

          {/* ============ LIMIT ============ */}
          {phase === "limit" && (
            <div className="p-10 sm:p-14 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-[#0B1C3F] flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-[#C9A84C]" />
              </span>
              <h3 className="mt-4 text-[22px] font-extrabold text-[#0B1C3F]">
                You've used all 3 free checks
              </h3>
              <p className="mt-2 text-[15px] text-slate-600 max-w-md mx-auto">
                Create a free TailorWiz account for unlimited resume scoring — plus the tools
                to tailor every resume to 90%+ before you apply.
              </p>
              <Link
                to="/pricing"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[15px] font-bold hover:bg-[#E5C36A] transition-colors"
              >
                See Plans & Get Started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default HomeResumeMatchTester;
